import { cn } from '@/utils/styles';
import {
  Carousel,
  CarouselNext,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
} from '@design-system/Carousel';
import { NutritionTipSkeleton } from './NutritionTipSectionSkeleton';
import { NutritionTips } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { EventTracker } from '@/utils';

export interface NutritionTipSectionProps extends React.ComponentProps<'div'> {
  tips?: NutritionTips[];
  isLoading?: boolean;
  isError?: boolean;
  'data-testid'?: string;
}

const GRADIENTS = [
  'bg-gradient-to-b from-[#E4E9EF] to-[#DAF2DC]',
  'bg-gradient-to-b from-[#E4EFE6] to-[#DAE8F2]',
  'bg-gradient-to-b from-[#EFECE4] to-[#F2DADA]',
  'bg-gradient-to-b from-[#EFE4E6] to-[#E0DAF2]',
  'bg-gradient-to-b from-[#F2E7DA] to-[#E4E9EF]',
];

const renderTips = (tips?: NutritionTips[]) => {
  if (!tips?.length) return null;

  const handleNutritionTips = () => {
    EventTracker.trackEvent('nutrition', 'nutrition_myNutritionTips');
  }
  
  const renderedTips = tips.map((tip, index) => (
    <CarouselItem key={`${tip}-${index}`} className="max-w-80 min-h-64" onClick={handleNutritionTips} data-testid={`nutrition-tips-card-${index}`}>
      <div className="p-1 w-full h-full">
        <div
          className={cn(
            'size-full select-none rounded-xxs px-5 py-8 typography-h3 !font-light text-secondary backdrop-blur flex flex-col justify-between',
            GRADIENTS[index % GRADIENTS.length],
          )}
        >
          {tip.nutrition_tips}
          {tip && tip?.based_on && (
            <p className="typography-caption1 text-tertiary mt-4">
              {tip?.based_on}
            </p>
          )}
        </div>
      </div>
    </CarouselItem>
  ));

  return renderedTips;
};

export const NutritionTipSection = ({
  tips,
  isLoading,
  isError,
  'data-testid': testId,
}: NutritionTipSectionProps) => {
  if (isLoading) {
    return <NutritionTipSkeleton />;
  }

  if (isError || !tips) {
    return;
  }

  return (
    <>
      <h2 className="mb-4 typography-h1 text-primary font-medium" data-testid={testId}>
        My Nutrition Tips
      </h2>

      <p className="mb-6 typography-body text-tertiary max-w-xl" data-testid="nutrition-tips-description">
        Based on your results, our team of nutritionists has gathered a few
        helpful tips and suggestions to support your daily choices.
      </p>

      <Carousel className="w-full relative" data-testid="nutrition-tips-carousel">
        <CarouselContent>{renderTips(tips)}</CarouselContent>

        <CarouselPrevious variant="ghost" />
        <CarouselNext variant="ghost" />
      </Carousel>
    </>
  );
};
